import {
  useState,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { IPreDefinedRange } from 'libs/design-system/src/types/date_range_picker';
import type { ITransactionsDateRange } from '@app/dashboard/types/innerLayout/transactions';
import DateRanges from '@app/dashboard/components/inner_layout/DateRanges';
import { mainPreDefinedRanges as preDefinedRanges } from '@app/dashboard/utils/dateRangePicker';
import { getLang } from '@app/dashboard/utils/localStorageUtils';
import { formatToISODate } from '@app/dashboard/utils/dateFormats';
import { Dropdown, DropdownButton, DropdownMenu, DropdownMenuItem, FormatCurrencyForDisplay } from '@unified-checkout/design-system';
import { useIntegrationsStore } from '@app/dashboard/store/integrations';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { COUNTRIES_INFO } from '@app/dashboard/constants/countries';
import { getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import styles from './transactions-header.module.scss';

interface TransactionsHeaderProps {
  setDateRange: (range: ITransactionsDateRange) => void;
  setAppliedRangeText: (text: IPreDefinedRange['label']) => void;
  selectedBranch: string | null;
  setSelectedBranch: (branch: string | null) => void;
  selectedCurrency: string | null;
  setSelectedCurrency: (currency: string | null) => void;
  branches: Array<{ id: number; name: string }>;
}

export const TransactionsHeader = ({
  setDateRange,
  setAppliedRangeText,
  selectedBranch,
  setSelectedBranch,
  selectedCurrency,
  setSelectedCurrency,
  branches,
}: TransactionsHeaderProps) => {
  const { t } = useTranslation();
  const lang = getLang();
  const { isLive } = useTestLiveModeStore();
  const {
    currencies,
    isLoading: isIntegrationsLoading,
  } = useIntegrationsStore();
  const currentMode = isLive ? 'live' : 'test';

  const defaultPreDefinedRange: IPreDefinedRange | undefined =
    preDefinedRanges(t)?.find((range) => range.default);

  const [appliedRanges, setAppliedRanges] = useState<{
    index?: number;
    name?: string;
    start?: Date;
    end?: Date;
  }>({
    index: defaultPreDefinedRange?.index,
    name: defaultPreDefinedRange?.name,
    start: defaultPreDefinedRange?.selectionRange[0],
    end: defaultPreDefinedRange?.selectionRange[1],
  });

  const { start: startDate, end: endDate, index: rangeIndex, name: rangeName } =
    appliedRanges;
  const comparedPreDefinedRangeLabel: IPreDefinedRange['label'] | undefined =
    preDefinedRanges(t)?.find((range) => range.index === rangeIndex)?.label ||
    '';

  useEffect(() => {
    if (startDate && endDate) {
      const formattedDateRange = formattedDates([
        startDate,
        endDate,
      ] as [Date, Date]);
      setDateRange(formattedDateRange);
    }

    setAppliedRangeText(comparedPreDefinedRangeLabel || '');
  }, [appliedRanges, lang]);

  const formattedDates = (range: [Date, Date]): ITransactionsDateRange => {
    const [formattedStartDate, formattedEndDate] = (range as [Date, Date])?.map(
      (date) => formatToISODate(date)
    );

    return { start_date: formattedStartDate, end_date: formattedEndDate };
  };

  const getDefaultCurrency = () => {
    const country = getFromLocalStorage(LOCAL_STORAGE_KEYS.COUNTRY);
    const countryCurrency = COUNTRIES_INFO[country]?.currency;

    const currency =
      currencies?.[currentMode]?.includes(countryCurrency)
        ? countryCurrency
        : currencies?.[currentMode]?.[0];

    return currency || null;
  };

  useEffect(() => {
    if (!selectedCurrency && currencies?.[currentMode]?.length) {
      const defaultCurrency = getDefaultCurrency();
      setSelectedCurrency(defaultCurrency);
    }
  }, [currencies, currentMode]);

  const localizeCurrency = (currency?: string) =>
    currency &&
    !t(`innerLayout.currencies.${currency}`).includes('innerLayout')
      ? t(`innerLayout.currencies.${currency}`)
      : currency;

  return (
    <div className={styles['transactions-header']}>
      <div className={styles['transactions-header__top']}>
        <h2 className={styles['transactions-header__title']}>
          {t('innerLayout.navbar.transactions')}
        </h2>
        <DateRanges
          appliedRanges={appliedRanges}
          handleRangesChange={(ranges) => setAppliedRanges(ranges)}
          lang={lang}
        />
      </div>
      <div className={styles['transactions-header__filters']}>
        <Dropdown width="fit-content">
          <DropdownButton
            text={
              selectedBranch === 'Main'
                ? t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.mainPaymeLink')
                : selectedBranch
                ? branches.find((b) => b.id.toString() === selectedBranch)
                    ?.name || t('innerLayout.paymePage.transactions.allBranches')
                : t('innerLayout.paymePage.transactions.allBranches')
            }
            placeholder={t('innerLayout.paymePage.transactions.selectBranch')}
            disabled={!branches || branches.length === 0}
          />
          <DropdownMenu width="fit-content" horizontalPlacement="start">
            <DropdownMenuItem
              text={t('innerLayout.paymePage.transactions.allBranches')}
              selected={!selectedBranch}
              onClickHandler={() => setSelectedBranch(null)}
            />
            <DropdownMenuItem
              text={t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.mainPaymeLink')}
              selected={selectedBranch === 'Main'}
              onClickHandler={() => setSelectedBranch('Main')}
            />
            {branches.map((branch) => (
              <DropdownMenuItem
                key={branch.id}
                text={branch.name}
                selected={selectedBranch === branch.id.toString()}
                onClickHandler={() => setSelectedBranch(branch.id.toString())}
              />
            ))}
          </DropdownMenu>
        </Dropdown>
        <Dropdown width="fit-content">
          <DropdownButton
            text={
              selectedCurrency
                ? (FormatCurrencyForDisplay(
                    localizeCurrency(selectedCurrency) || selectedCurrency
                  ) as any)
                : t('innerLayout.homePage.overview.currencies.selectCurrency')
            }
            placeholder={t(
              'innerLayout.homePage.overview.currencies.selectCurrency'
            )}
            disabled={!currencies || isIntegrationsLoading || !currencies?.[currentMode]?.length}
          />
          <DropdownMenu width="fit-content" horizontalPlacement="start">
            {currencies?.[currentMode]?.map((currency, i: number) => {
              const localizedCurrency = localizeCurrency(currency);
              const menuItemText = FormatCurrencyForDisplay(
                localizedCurrency
              ) as any;

              return (
                <DropdownMenuItem
                  key={`currency-${i}-${currency}`}
                  text={menuItemText}
                  selected={selectedCurrency === currency}
                  onClickHandler={() => setSelectedCurrency(currency)}
                />
              );
            })}
          </DropdownMenu>
        </Dropdown>
      </div>
    </div>
  );
};

